/**
 * <pre>
 * Goal config.
 *
 * Summary of responsibilities:
 *
 *  - defines any commonly used config values specific to Goal.
 * </pre>
 *
 * @author Tim Loram
 *
 * @memberof module:Goal
 * @namespace
 *
 * @type {Object}
 */
const Config = {
  /**
   * The publicly visible name to use when exposing this apps api methods.
   * @default
   */
  publicName: 'Goal',

  /**
   * The key to use when creating the root store using combineReducers.
   * @default
   */
  stateKey: 'goal',

  /**
   * The key to use when storing things in localstorage/cookies.
   * @default
   */
  storageKey: 'ngxd.goal',

  /**
   * API endpoints.
   *
   * @type {Object}
   * @property {String} participate The endpoint for particpating in a goal.
   * @property {String} progress The endpoint for retrieving goal progress.
   */
  api: {
    participate: 'api/interact/d/metric/record/{METRIC_ID}.json',
    progress:    'api/interact/d/metric/get/{METRIC_ID}.json',
  },

  /**
   * Intervals.
   *
   * @type {Object}
   * @property {Number} state Interval in milliseconds for running 'tick' logic (ie recalculating state of application based on current datetime, progress etc).
   * @property {Number} progress Base interval in milliseconds for how often to check async goal/milestone progress and increase corresponding progress bar (NOTE: this value will be increase/decay dynamically based on vote frequency between checks).
   * @property {Number} progressMin Increasing progress intervals will never exceed this lower boundary.
   * @property {Number} progressMax Decaying progress intervals will never exceed this upper boundary.
   * @property {Number} decay The amount of time in milliseconds to decay or increase based on participation rate calculation.
   * @property {Number} localProgress Interval in milliseconds for how often to check local progress (eg. time window progression) (Can be run more frequently as it doesn't require an async request to the server).
   */
  intervals: {
    // Default: 1/4 second
    state: 1000 / 4,

    // Default: 60 seconds
    progress: 1000 * 60,

    // Default: 30 seconds
    progressMin: 1000 * 30,

    // Default: 30 minutes
    progressMax: 1000 * 60 * 30,

    // Default: 10 seconds
    decay: 1000 * 10,

    // Default: 1 second
    localProgress: 1000,
  },

  /**
   * Participation statistics.
   *
   * @type {Object}
   * @property {Number} maxHistoryAge Trim entries from the history when they become older than this, so they are not included in statistic calculations.
   */
  statistics: {
    // Default: 10 minutes
    maxHistoryAge: 1000 * 60 * 10,
  },

  /**
   * Misc formatting related options.
   *
   * @type {Object}
   * @property {Number} decimalPlaces Default number of decimal places to show when outputting percentage.
   */
  formatting: {
    decimalPlaces: 0,
  },
};

export default Config;
